import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/api";
import "./AppointmentDetailView.css";

const AppointmentDetailView = () => {
  const { appointmentId } = useParams();
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAppointmentDetails = useCallback(async () => {
    if (!patientUser) {
      setError("No user logged in. Please log in as a patient.");
      navigate('/login', { replace: true });
      return;
    }

    if (patientUser.role !== "patient") {
      setError("This page is for patients only. Redirecting...");
      navigate('/doctor-dashboard', { replace: true });
      return;
    }

    try {
      const appointmentRes = await api.get(`/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });

      const patientIdFromData = appointmentRes.data.patientId._id || appointmentRes.data.patientId;
      if (patientIdFromData !== patientUser.id) {
        throw new Error("Unauthorized: You can only access your own appointments");
      }

      setAppointment(appointmentRes.data);

      try {
        const prescriptionRes = await api.get(`/prescriptions/appointment/${appointmentId}`, {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        });
        setPrescription(prescriptionRes.data);
      } catch (presErr) {
        setPrescription(null);
      }
    } catch (err) {
      console.error("Error fetching appointment:", err.message || err.response?.data?.message);
      if (err.message.includes("Unauthorized") || err.response?.status === 403) {
        setError("You are not authorized to view this appointment.");
        navigate('/patient/appointments', { replace: true });
      } else if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        navigate('/login', { replace: true });
      } else if (err.response?.status === 404) {
        setError("Appointment not found.");
      } else {
        setError(`Failed to load appointment details: ${err.message || err.response?.data?.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, patientUser, navigate]);

  useEffect(() => {
    fetchAppointmentDetails();
  }, [fetchAppointmentDetails]);

  const handleDownload = async (prescriptionData) => {
    if (!prescriptionData?.pdfPath) {
      setError("No file path available for download.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const response = await api.get(`/appointments/prescriptions/${prescriptionData._id}/download-direct`, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        responseType: 'blob', // Handle binary data
      });

      const filename = `Prescription-${new Date(prescriptionData.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      const errorDetails = error.response?.data?.details || 'No additional details available';
      setError(`Failed to download prescription: ${errorMessage} - ${errorDetails}`);
      setTimeout(() => setError(null), 5000);
      console.error("Download error:", error);
    }
  };

  const handleFeedback = () => {
    if (appointment?.status.toLowerCase() === "completed") {
      navigate(`/patient/feedback/${appointmentId}`);
    } else {
      setError("Feedback can only be submitted for completed appointments.");
    }
  };

  const calculateRefundAmount = (totalFee) => Math.max(totalFee - 50, 0);

  if (isLoading) return <div className="adv-loading">Loading appointment details...</div>;
  if (error) return (
    <div className="adv-container">
      <main className="adv-main">
        <div className="adv-error-card">
          <h1 className="adv-title">Error</h1>
          <p className="adv-text">{error}</p>
          <button className="adv-button" onClick={() => navigate(`/patient/appointments`)}>
            Back to Appointments
          </button>
        </div>
      </main>
    </div>
  );

  if (!appointment) return null;

  return (
    <div className="adv-container">
      <main className="adv-main">
        <div className="adv-header">
          <h1 className="adv-title">Appointment Details - {appointment._id}</h1>
          <span className={`adv-status-badge ${appointment.status.toLowerCase()}`}>
            {appointment.status}
          </span>
        </div>

        <div className="adv-grid">
          <section className="adv-card">
            <h2 className="adv-subtitle">Appointment Information</h2>
            <div className="adv-details">
              <p><strong>ID:</strong> {appointment._id}</p>
              <p><strong>Booking Date:</strong> {new Date(appointment.createdAt).toLocaleString()}</p>
              <p><strong>Consultation Date:</strong> {new Date(appointment.date).toLocaleString()}</p>
              <p><strong>Symptoms:</strong> {appointment.symptoms || "N/A"}</p>
              <p><strong>Call Duration:</strong> {appointment.callDuration || "N/A"}</p>
              <p><strong>Completed At:</strong> {appointment.completedAt ? new Date(appointment.completedAt).toLocaleString() : "N/A"}</p>
              <p><strong>Notes:</strong> {appointment.notes || "No notes provided"}</p>
            </div>
          </section>

          <section className="adv-card">
            <h2 className="adv-subtitle">Doctor Details</h2>
            <div className="adv-details">
              <p><strong>ID:</strong> {appointment.doctorId?._id || appointment.doctorId}</p>
              <p><strong>Name:</strong> Dr. {appointment.doctorId?.name || "Unknown"}</p>
              <p><strong>Specialization:</strong> {appointment.doctorId?.specialization || "N/A"}</p>
            </div>
          </section>

          <section className="adv-card">
            <h2 className="adv-subtitle">Patient Details</h2>
            <div className="adv-details">
              <p><strong>ID:</strong> {appointment.patientId?._id || appointment.patientId}</p>
              <p><strong>Name:</strong> {appointment.name || appointment.patientId?.name || "You"}</p>
              <p><strong>Age:</strong> {appointment.age || "N/A"}</p>
              <p><strong>Gender:</strong> {appointment.gender || "N/A"}</p>
            </div>
          </section>

          <section className="adv-card">
            <h2 className="adv-subtitle">Status</h2>
            <div className="adv-details">
              <p><strong>Appointment Status:</strong> {appointment.status || "N/A"}</p>
              <p><strong>Prescription Status:</strong> {appointment.prescriptionStatus || "Not Yet Provided"}</p>
            </div>
          </section>

          {prescription && (
            <section className="adv-card">
              <h2 className="adv-subtitle">Prescription Information</h2>
              <div className="adv-details">
                <p><strong>ID:</strong> {prescription._id}</p>
                <p><strong>Medications:</strong> {prescription.medications?.map(med => `${med.name} (${med.dosage})`).join(", ") || "None"}</p>
                <p><strong>Instructions:</strong> {prescription.instructions || "N/A"}</p>
                <p><strong>Next Appointment:</strong> {prescription.nextAppointmentDate ? new Date(prescription.nextAppointmentDate).toLocaleString() : "N/A"}</p>
                <p><strong>Created:</strong> {new Date(prescription.createdAt).toLocaleString()}</p>
                {appointment.prescriptionStatus === "Completed" && (
                  <button
                    className="adv-action-btn"
                    onClick={() => handleDownload(prescription)}
                    aria-label="Download prescription PDF"
                  >
                    Download Prescription PDF
                  </button>
                )}
              </div>
            </section>
          )}

          <section className="adv-card">
            <h2 className="adv-subtitle">Medical Records</h2>
            <div className="adv-details">
              {appointment.records && appointment.records.length > 0 ? (
                appointment.records.map((record, index) => (
                  <div key={index}>
                    <p><strong>File:</strong> {record.filename}</p>
                    <p><strong>Path:</strong> {record.path}</p>
                    <p><strong>Uploaded:</strong> {new Date(record.uploadedAt).toLocaleString()}</p>
                  </div>
                ))
              ) : (
                <p>No records uploaded</p>
              )}
            </div>
          </section>

          <section className="adv-card">
            <h2 className="adv-subtitle">Payment Details</h2>
            <div className="adv-details">
              <p><strong>Total Consultation Fee:</strong> ₹{appointment.totalFee || "N/A"}</p>
              <p><strong>Payment Status:</strong>
                <span className={`adv-payment-status ${appointment.paymentStatus?.toLowerCase()}`}>
                  {appointment.paymentStatus || "Pending"}
                </span>
              </p>
              {appointment.paymentStatus === "paid" && appointment.bookingStatus === "booked" && (
                <>
                  <p><strong>Transaction ID:</strong> {appointment.paymentDetails?.paymentId || "N/A"}</p>
                  <p><strong>Paid At:</strong> {appointment.paymentDetails?.paidAt ? new Date(appointment.paymentDetails.paidAt).toLocaleString() : "N/A"}</p>
                  <p><strong>Order ID:</strong> {appointment.paymentDetails?.orderId || "N/A"}</p>
                </>
              )}
              {appointment.status === "canceled" && appointment.paymentStatus === "refunded" && (
                <>
                  <p><strong>Cancellation Fee:</strong> ₹{appointment.refundDetails?.cancellationFee || 50}</p>
                  <p><strong>Refund Amount:</strong> ₹{appointment.refundDetails?.amount || calculateRefundAmount(appointment.totalFee)}</p>
                </>
              )}
            </div>
          </section>

          {appointment.status === "canceled" && appointment.refundStatus === "Processed" && (
            <section className="adv-card adv-refund-card">
              <h2 className="adv-subtitle">Refund Details</h2>
              <div className="adv-details">
                <p><strong>Refund Processing:</strong> Takes 3-5 working days</p>
                <p><strong>Refund Status:</strong> Refund Processed</p>
                <p><strong>Total Fee Paid:</strong> ₹{appointment.totalFee || "N/A"}</p>
                <p><strong>Cancellation Fee:</strong> ₹{appointment.refundDetails?.cancellationFee || 50}</p>
                <p><strong>Refunded Amount:</strong> ₹{appointment.refundDetails?.amount || calculateRefundAmount(appointment.totalFee)}</p>
                <p><strong>Refund Processed At:</strong> {appointment.refundedAt ? new Date(appointment.refundedAt).toLocaleString() : "N/A"}</p>
                <p><strong>Refund ID:</strong> {appointment.refundDetails?.refundId || "N/A"}</p>
                <p className="adv-refund-note">
                  [Amount will be credited to your bank account within 5-7 working days after the refund has been processed]
                </p>
                <p><strong>Notes:</strong></p>
                <ul className="adv-refund-notes">
                  <li>appointmentId: {appointment._id}</li>
                  <li>reason: Appointment canceled by admin/patient</li>
                </ul>
              </div>
            </section>
          )}

          {appointment.status.toLowerCase() === "completed" && (
            <section className="adv-card">
              <h2 className="adv-subtitle">Doctor Feedback</h2>
              <button className="adv-action-btn" onClick={handleFeedback}>
                Submit Feedback
              </button>
            </section>
          )}
        </div>

        <button className="adv-back-btn" onClick={() => navigate(`/patient/appointments`)}>
          Back to Appointments
        </button>
      </main>
    </div>
  );
};

export default AppointmentDetailView;